import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { testimonials, transformations, stats } from '../mockData';
import './Home.css';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b" 
            alt="Fitness transformation"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="display-large fade-in">
              Transform Your Body.<br />
              <span className="text-red">Redefine Your Confidence.</span>
            </h1>
            <p className="body-large mt-lg fade-in" style={{animationDelay: '0.2s'}}>
              Sympozium helps you achieve lasting weight loss through science-backed training and personalized coaching.
            </p>
            <div className="hero-cta mt-xl fade-in" style={{animationDelay: '0.4s'}}>
              <Link to="/contact" className="btn btn-primary btn-large">
                Start Your Free 2-Week Transformation
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="hero-trust mt-lg fade-in" style={{animationDelay: '0.6s'}}>
              <div className="trust-item">
                <CheckCircle size={20} className="trust-icon" />
                <span>Guaranteed Results</span>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} className="trust-icon" />
                <span>Science-Backed Methods</span>
              </div>
              <div className="trust-item">
                <CheckCircle size={20} className="trust-icon" />
                <span>Improve Health & Longevity</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={stat.id} className="stat-card fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section section">
        <div className="container">
          <div className="philosophy-content">
            <div className="philosophy-text slide-in-left">
              <h2 className="heading-1 mb-lg">Our Philosophy</h2>
              <p className="body-large mb-md">
                At Sympozium, we believe transformation goes beyond the gym. It's about discipline, mindset, and balance.
              </p>
              <p className="body-medium mb-lg text-gray">
                Our science-backed approach combines personalized training, nutrition guidance, and accountability to help you achieve lasting results. We don't believe in quick fixes or fad diets—we're here to help you build sustainable habits that transform your life.
              </p>
              <div className="philosophy-features">
                <div className="feature-item">
                  <CheckCircle size={24} className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Science-Backed Training</h4>
                    <p className="body-small text-gray">Evidence-based methods proven to deliver results</p>
                  </div>
                </div>
                <div className="feature-item">
                  <CheckCircle size={24} className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Personalized Approach</h4>
                    <p className="body-small text-gray">Custom plans tailored to your unique goals and lifestyle</p>
                  </div>
                </div>
                <div className="feature-item">
                  <CheckCircle size={24} className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Accountability & Support</h4>
                    <p className="body-small text-gray">Continuous guidance to keep you on track</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="philosophy-image slide-in-right">
              <img 
                src="https://images.unsplash.com/photo-1526506118085-60ce8714f8c5" 
                alt="Personal training"
                className="philosophy-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Transformations Gallery */}
      <section className="transformations-section section-large">
        <div className="container">
          <div className="section-header text-center mb-2xl">
            <h2 className="heading-1 mb-md">Real Results, Real People</h2>
            <p className="body-large text-gray">See the transformations our clients have achieved</p>
          </div>
          <div className="transformations-grid">
            {transformations.map((transformation, index) => (
              <div key={transformation.id} className="transformation-card fade-in" style={{animationDelay: `${index * 0.15}s`}}>
                <div className="transformation-image-wrapper">
                  <img 
                    src={transformation.image} 
                    alt={transformation.name}
                    className="transformation-image"
                  />
                  <div className="transformation-overlay">
                    <div className="transformation-result">
                      <span className="result-text">{transformation.result}</span>
                    </div>
                  </div>
                </div>
                <div className="transformation-info">
                  <h4 className="heading-3">{transformation.name}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section section">
        <div className="container">
          <div className="section-header text-center mb-2xl">
            <h2 className="heading-1 mb-md">What Our Clients Say</h2>
            <p className="body-large text-gray">Join the community of satisfied clients who transformed their lives</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.id} className="testimonial-card card card-hover fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="testimonial-stars mb-md">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--brand-red)" stroke="var(--brand-red)" />
                  ))}
                </div>
                <p className="body-medium mb-lg">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <h4 className="heading-3">{testimonial.name}</h4>
                  <p className="body-small text-gray">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-large">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="heading-1 mb-lg">Ready to Start Your Transformation?</h2>
              <p className="body-large mb-xl text-gray">
                Join Sympozium today and get your first 2 weeks free. No commitment required.
              </p>
              <Link to="/contact" className="btn btn-primary btn-large">
                Start Your Free Trial
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;